/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";

import {EmGroup} from '../../models/em-group';
import {GroupType} from '../../models/group-type';
import {InstanceData} from "../../models/instance-data";
import { ParentLocationWrapper } from "../../models/jaxb/parent-location-wrapper";
import {Location} from "../../models/location";
import {LocationInstance} from "../../models/location-instance";

import {GroupService} from '../../services/group.service';
import {LocationService} from "../../services/location.service";
import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE} from "../../app-constants";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { FilterOption, FilterType, REMOVE_FILTER } from '../../models/filter-option';


@Component({
  templateUrl: './locations.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class LocationsComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mLocations: LocationUI[] = [];
  mLocationInstances: LocationInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Locations, 2 = Location Instances


  //Entity Type Dialog Variables
  mEntity: Location;
  mEntityName: string;
  mEntityType = GroupType.LOCATION;
  mEntityTypeName = "Location";

  // All the Filter Options available
  filterOptionForLocation: FilterOption[] = [];
  filterOptionForLocationInstances: FilterOption[] = [];

  // The Filter Option Selected by the user
  selectedFilterOptionForLocation: FilterOption = REMOVE_FILTER;
  selectedFilterOptionForLocationInstance: FilterOption = REMOVE_FILTER;
  /** Active filters for the Location Instances (Name + Type), AND'd together */
  activeFilterOptionsForLocationInstance: FilterOption[] = [];


  //Entity Instance Dialog Variables
  mInstance: LocationInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: Location;
  mInstanceType = GroupType.LOCATIONINSTANCE;
  mInstanceTypeName = "Location";
  mAvailableEntityTypes: Location[];
  mSelectedLocation: LocationInstance;
  mAvailableLocations: LocationInstance[];

  currentPageForLocation = 1;
  currentPageForLocationInstance = 1;

  totalNumberOfLocation = 0;
  totalNumberOfLocationInstances = 0;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mLocationService: LocationService,
    private mGroupService: GroupService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) { }



  ngOnInit(): void {
    this.refreshFilterOptionsForLocation();
    this.refreshFilterOptionsForLocationInstance();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data: { 
        locations: PagedResults<Location>, 
        locationInstances: PagedResults<LocationInstance>}
      ) => {
        this.updatePageLocation(data.locations);
        this.updatePageLocationInstance(data.locationInstances);
    });
  }

  private updatePageLocation(pagedData: PagedResults<Location>): void {
    const uiObjects: LocationUI[] = []
    if(pagedData.data != null) {
        pagedData.data.forEach(Location => {
        const ui = new LocationUI();
        ui.mLocation = Location;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }
    this.mLocations = uiObjects;

    this.totalNumberOfLocation = pagedData.size;
  }

  private updatePageLocationInstance(pagedData: PagedResults<LocationInstance>): void {
    const uiObjects: LocationInstanceUI[] = []
    if(pagedData.data != null) {
        pagedData.data.forEach(Location => {
        const ui = new LocationInstanceUI();
        ui.mLocationInstance = Location;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }
    this.mLocationInstances = uiObjects;

    this.totalNumberOfLocationInstances = pagedData.size;
  }

  manageResultPerPageClickedForTypes(value: number): void{
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForLocation = 1;
    this.refreshLocationInstance();
  }

  manageResultPerPageClickedForInstances(value: number): void {
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForLocationInstance = 1;
    this.refreshLocationInstance();
  }


  isSingleLocationSelected(): boolean {
    if (this.mLocations != null && this.mLocations.length > 0) {
      let oneSelected = false;
      for (const location of this.mLocations) {
        if (location.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyLocationsSelected(): boolean {
    return this.mLocations.some(location => location.mSelected);
  }

  isAllLocationsSelected(): boolean {
    return this.mLocations !== null && this.mLocations.every(location => location.mSelected);
  }

  setAllLocationsSelected(event: any): void {
    this.mLocations.forEach(location => location.mSelected = event.target.checked);
  }





  isSingleLocationInstanceSelected(): boolean {
    if (this.mLocationInstances != null && this.mLocationInstances.length > 0) {
      let oneSelected = false;
      for (const locationInstance of this.mLocationInstances) {
        if (locationInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyLocationInstancesSelected(): boolean {
    return this.mLocationInstances.some(locationInstance => locationInstance.mSelected);
  }

  isAllLocationInstancesSelected(): boolean {
    return this.mLocationInstances !== null && this.mLocationInstances.every(locationInstance => locationInstance.mSelected);
  }

  setAllLocationInstancesSelected(event: any): void {
    this.mLocationInstances.forEach(locationInstance => locationInstance.mSelected = event.target.checked);
  }





  getSelectedLocations(): Location[] {
    const locations: Location[] = [];
    this.mLocations.filter(ui => ui.mSelected).forEach(ui => locations.push(ui.mLocation));
    return locations;
  }

  getSelectedLocationIds(): number[] {
    const ids: number[] = [];
    this.mLocations.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mLocation.id));
    return ids;
  }

  getSelectedLocationNames(): string[] {
    const names: string[] = [];
    this.mLocations.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mLocation.name));
    return names;
  }





  getSelectedLocationInstances(): LocationInstance[] {
    const locationInstances: LocationInstance[] = [];
    this.mLocationInstances.filter(ui => ui.mSelected).forEach(ui => locationInstances.push(ui.mLocationInstance));
    return locationInstances;
  }

  getSelectedLocationInstanceIds(): number[] {
    const ids: number[] = [];
    this.mLocationInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mLocationInstance.id));
    return ids;
  }

  getSelectedLocationInstanceNames(): string[] {
    const names: string[] = [];
    this.mLocationInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mLocationInstance.name));
    return names;
  }





  manageLocationGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.LOCATION);
  }

  manageLocationInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.LOCATIONINSTANCE);
  }





  addSelectedLocationsToGroupClicked(): void {
    if (this.isAnyLocationsSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.LOCATION, this.getSelectedLocationNames());
    }
  }

  addSelectedLocationInstancesToGroupClicked(): void {
    if (this.isAnyLocationInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.LOCATIONINSTANCE, this.getSelectedLocationInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.LOCATION: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedLocationIds())
          .then(
            success => {
              this.refreshLocations();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.LOCATIONINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedLocationInstanceIds())
          .then(
            success => {
              this.refreshLocationInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedLocationsFromGroupClicked(): void {
    if (this.isAnyLocationsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const locationNames: string[] = [];
      this.getSelectedLocations().forEach(location => {
        locationNames.push(location.name);
        if (location.groups != null) {
          location.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, locationNames);
    }
  }

  removeSelectedLocationInstancesFromGroupClicked(): void {
    if (this.isAnyLocationInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const locationInstanceNames: string[] = [];
      this.getSelectedLocationInstances().forEach(locationInstance => {
        locationInstanceNames.push(locationInstance.name);
        if (locationInstance.groups != null) {
          locationInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, locationInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.LOCATION: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedLocationIds())
          .then(
            success => {
              this.refreshLocations();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.LOCATIONINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedLocationInstanceIds())
          .then(
            success => {
              this.refreshLocationInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.LOCATION: {
        this
          .mGroupService
          .getAllLocationsInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.LOCATIONINSTANCE: {
        this
          .mGroupService
          .getAllLocationInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  addLocationClicked(): void {
    this.mEntity = new Location();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedLocationClicked(): void {
    if (this.isSingleLocationSelected()) {
      this.editLocation(this.getSelectedLocations()[0]);
    }
  }

  duplicateSelectedLocationClicked(): void {
    if (this.isSingleLocationSelected()) {
      this
        .mLocationService
        .getLocationById(this.getSelectedLocations()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedLocationsClicked(): void {
    if (this.isAnyLocationsSelected()) {
      this.mDeleteFlag = 1; //1 = Locations
      this.mConfirmDeleteDialog.show("location type", this.getSelectedLocationNames());
    }
  }

  editLocation(location: Location): void {
    this
      .mLocationService
      .getLocationById(location.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.entityParent = response.parent;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
              this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Location... Create it
      this
        .mLocationService
        .createLocation(this.mEntity)
        .then(
          response => {
            this.refreshLocations();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Location... Update it
      this
        .mLocationService
        .updateLocation(this.mEntity)
        .then(
          response => {
            this.refreshLocations();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }





  addLocationInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new LocationInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataRefreshParentLocationsAndShowDialog();
  }

  editSelectedLocationInstanceClicked(): void {
    if (this.isSingleLocationInstanceSelected()) {
      this.editLocationInstance(this.getSelectedLocationInstances()[0]);
    }
  }

  duplicateSelectedLocationInstanceClicked(): void {
    if (this.isSingleLocationInstanceSelected()) {
      this
      .mLocationService
      .getLocationInstanceById(this.getSelectedLocationInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshParentLocationsAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedLocationInstancesClicked(): void {
    if (this.isAnyLocationInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Location Instances
      this.mConfirmDeleteDialog.show("location", this.getSelectedLocationInstanceNames());
    }
  }

  editLocationInstance(locationInstance: LocationInstance): void {
    this
      .mLocationService
      .getLocationInstanceById(locationInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshParentLocationsAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataRefreshParentLocationsAndShowDialog(): Promise<void> {
    //Create a List of the available Locations that the Instance can be a type of
    this.mAvailableEntityTypes = await this.mLocationService.getAllLocations();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.location == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.location.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.location.functions;
      this.mInstanceEntityType.properties = this.mInstance.location.properties;
    }

    //Refresh the available and selected Parent Locations
    this
      .mLocationService
      .getAllLocationInstances()
      .then(
        response => {
          //Set the possible Parent Location values so that they can be used by the EntityInstanceDialogComponent
          this.mAvailableLocations = response;

          //If we are editing an existing Location Instance, remove itself from the available Parents since it cannot be it's own parent
          if (this.mInstance != null && this.mInstance.id != null) {
            this.mAvailableLocations = this.mAvailableLocations.filter(location => location.id != this.mInstance.id);
          }

          //Figure out if a Parent Location was previously selected for the Location Instance, if so get the correct version to be modeled and store it
          this.mSelectedLocation = this.mInstance == null || this.mInstance.parent == null || this.mInstance.parent.instance == null? null : this.mAvailableLocations.find(location => location.id == this.mInstance.parent.instance.id);

          // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
          this.cdr.detectChanges();

          //Refresh the Instance Data, loading the existing values in for the Instance being edited
          this.mEntityInstanceDialogComponent.refreshData();

          //Show the Entity Instance Dialog
          this.mEntityInstanceDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Location Instance to be created
    this.mInstance.location = this.mInstanceEntityType;

    if (this.mSelectedLocation != null) {
      this.mInstance.parent = new ParentLocationWrapper();
      this.mInstance.parent.instance = this.mSelectedLocation;
    }

    if (this.mInstance.id == null) {
      //ID is null, Location Instance does not exist!  Create it...
      this
        .mLocationService
        .createLocationInstance(this.mInstance)
        .then(
          response => {
            this.refreshLocationInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Location Instance exists!  Update it...
      this
        .mLocationService
        .updateLocationInstance(this.mInstance)
        .then(
          response => {
            this.refreshLocationInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: Location): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mLocationService
        .getLocationById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  refreshInstanceData(): void {
    this
      .mLocationService
      .getLocationInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }

  selectedLocationChanged(location: LocationInstance): void {
    this.mSelectedLocation = location;
  }





  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Locations
      this
        .mLocationService
        .deleteLocations(this.getSelectedLocationIds())
        .then(
          response => {
            this.refreshLocations();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Location Instances
      this
        .mLocationService
        .deleteLocationInstances(this.getSelectedLocationInstanceIds())
        .then(
          response => {
            this.refreshLocationInstance();
          },
          error => this.handleError(error)
        );
    }
  }

 
  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

    /*-----------------------FILTER FUNCTIONS FOR Location -------------------------*/

    private refreshFilterOptionsForLocation(): void {
      this.filterOptionForLocation = [
        REMOVE_FILTER,
        new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
      ];
    }

    // Called by <app-filter-bar> whenever the active filter or its value changes.
    onLocationFilterChanged(option: FilterOption): void {
      this.selectedFilterOptionForLocation = option;
      this.currentPageForLocation = 1;
      this.refreshLocations();
    }
  
    filterStringChangedLocations(pageNumber?: number): void {
      if (!pageNumber) { pageNumber = 1; }
      // Show only size on this new page
      console.log("page Changed to " + pageNumber);
      this.currentPageForLocation = pageNumber;
  
      this.pageChangedLocation(pageNumber);
  
    }
  
    /*----------------------- FILTER FUNCTIONS FOR Location INSTANCES -------------------------*/
  
    private refreshFilterOptionsForLocationInstance(): void {
      this.filterOptionForLocationInstances = [
        REMOVE_FILTER,
        new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
        new FilterOption({ column: "location.name", label: "Type", type: FilterType.STRING }),
      ];
    }

    // Called by <app-filter-bar> whenever the active filter or its value changes.
    onLocationInstanceFilterChanged(option: FilterOption): void {
      this.selectedFilterOptionForLocationInstance = option;
      this.currentPageForLocationInstance = 1;
      this.refreshLocationInstance();
    }
  
    filterStringChangedLocationInstances(pageNumber?: number): void {
      if (!pageNumber) { pageNumber = 1; }
      // Show only size on this new page
      console.log("page Changed to " + pageNumber);
      this.currentPageForLocationInstance = pageNumber;
  
      this.pageChangedLocationInstance(pageNumber);
  
    }
  
      /******************** PAGINATOR FUNCTIONS FOR Location  *****************/
  
      pageChangedLocation(pageNumber: number): void {
        if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
          return;
        }
    
        if(this.currentPageForLocation == pageNumber) {
          return;
        }
    
        this.currentPageForLocation = pageNumber;
        this.refreshLocations();
      }
    
    
      private async refreshLocations(): Promise<void> {
        this.spinnerService.show();
        let pagedData: PagedResults<Location>;
        try {
          pagedData = await this.mLocationService.getAllLocationsForPaging((this.currentPageForLocation - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.selectedFilterOptionForLocation.apiValue);
        } catch (error) {
          this.spinnerService.hide();
          this.handleError(error);
          return;
        }
    
        this.updatePagedLocation(pagedData);
        this.spinnerService.hide();
      }
    
    
      private updatePagedLocation(pagedData: PagedResults<Location>): void {
        const uiObjects: LocationUI[] = [];
        if(pagedData.data != null) {
          pagedData.data.forEach(Location => {
            const ui = new LocationUI();
            ui.mLocation = Location;
            ui.mSelected = false;
            uiObjects.push(ui);
          })
        }
        this.mLocations = uiObjects;
    
        this.totalNumberOfLocation = pagedData.size;
      }
    
      /******************** PAGINATOR FUNCTIONS FOR Location INSTANCES *****************/
  
    pageChangedLocationInstance(pageNumber: number): void {
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      if(this.currentPageForLocationInstance == pageNumber) {
        return;
      }
  
      this.currentPageForLocationInstance = pageNumber;
      this.refreshLocationInstance();
    }
  
    private async refreshLocationInstance(): Promise<void> {
      this.spinnerService.show();
      let pagedData: PagedResults<LocationInstance>;
      try {
        pagedData = await this.mLocationService.getAllLocationInstancesForPagingWithFilters((this.currentPageForLocationInstance - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFilterOptionsForLocationInstance);
      } catch (error) {
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
  
      this.updatePagedLocationInstance(pagedData);
      this.spinnerService.hide();
    }
  
  
    private updatePagedLocationInstance(pagedData: PagedResults<LocationInstance>): void {
      const uiObjects: LocationInstanceUI[] = [];
      if(pagedData.data != null) {
        pagedData.data.forEach(LocationInstance => {
          const ui = new LocationInstanceUI();
          ui.mLocationInstance = LocationInstance;
          ui.mSelected = false;
          uiObjects.push(ui);
        })
      }
      this.mLocationInstances = uiObjects;
  
      this.totalNumberOfLocationInstances = pagedData.size;
    }
  
  /*  
   *  Handling the Right click event only on Locations Instance Name Column
 */
  fetchFunctionList(event: MouseEvent, locationInstance: LocationInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = [];

    // Send the request to get the Locations Instance details using the ID
    this
      .mLocationService
      .getLocationInstanceById(locationInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the General Function Dialog to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.location && Array.isArray(response.location.functions) && (response.location.functions).length > 0) {
              this.functionList = response.location.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    // If its a No Function Available
    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }


}

class LocationUI {
  mSelected = false;
  mLocation: Location;
}

class LocationInstanceUI {
  mSelected = false;
  mLocationInstance: LocationInstance;
}